import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { useLivePageContent } from '@/hooks/usePageContent';
import { Reveal, RevealGroup, motion, fadeUp } from '@/components/public/anim';
import { Seo } from '@/components/Seo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Clock, Route as RouteIcon, ArrowRight, Ticket, Castle } from 'lucide-react';

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

const HERO_IMG = 'https://images.pexels.com/photos/2422461/pexels-photo-2422461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1920';
const FALLBACK = 'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600';

const fmtDuration = (min) => {
  if (!min) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
};

function TourCard({ tour }) {
  const stops = tour.itinerary_count || (tour.itinerary || []).length;
  const dur = fmtDuration(tour.duration_min);
  return (
    <motion.div variants={fadeUp} data-testid={`tour-card-${tour.slug}`}>
      <Card className="group flex h-full flex-col overflow-hidden rounded-[24px] border-border bg-card shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]">
        <div className="relative h-52 overflow-hidden bg-muted">
          <img
            loading="lazy"
            src={tour.cover_image ? mediaUrl(tour.cover_image) : FALLBACK}
            alt={tour.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {tour.category && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.92)] px-3 py-1 text-xs font-semibold text-[hsl(var(--brand-bark))]">
              <Castle className="h-3.5 w-3.5" />{tour.category}
            </span>
          )}
          {typeof tour.from_price === 'number' && (
            <span className="absolute bottom-3 right-3 rounded-full bg-background/90 px-3 py-1 text-sm font-bold text-foreground shadow-sm backdrop-blur">
              {tour.from_price === 0 ? 'Gratis' : `da €${Number(tour.from_price).toFixed(0)}`}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-display text-xl font-bold leading-snug">{tour.title}</h3>
          {tour.short_desc && <p className="mt-2 line-clamp-2 flex-1 text-sm text-muted-foreground">{tour.short_desc}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            {stops > 0 && <span className="inline-flex items-center gap-1"><RouteIcon className="h-3.5 w-3.5 text-primary" />{stops} tappe</span>}
            {dur && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" />{dur}</span>}
            {tour.location?.name && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" />{tour.location.name}</span>}
          </div>
          <Button asChild className="mt-5 w-full rounded-full" data-testid={`tour-cta-${tour.slug}`}>
            <Link to={`/esperienze/${tour.slug}`}>Scopri e prenota <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}

export default function TourBorghi() {
  const [tours, setTours] = useState(null);
  const ov = useLivePageContent('tour-nei-borghi');

  useEffect(() => {
    api.get('/experience/public/experiences', { params: { type: 'tour' } })
      .then(({ data }) => setTours(data?.experiences || []))
      .catch(() => setTours([]));
  }, []);

  return (
    <PublicLayout>
      <Seo path="/tour-nei-borghi" title="Tour nei Borghi — Sila Experience" description="Itinerari di una giornata tra i borghi silani, tra storia, tradizioni e sapori. Prenota il tuo tour online." />
      <SilaHero
        image={str(ov.heroImage) || HERO_IMG}
        badge={str(ov.eyebrow) || 'Tour dei Borghi'}
        title={str(ov.title) || 'Tour nei Borghi della Sila'}
        subtitle={str(ov.lead) || 'Itinerari di una giornata tra borghi autentici, laghi e sapori. Più tappe, più emozioni: un unico biglietto per vivere la Sila.'}
        icon={Castle}
        minH="56vh"
        testId="tour-borghi-hero"
      />

      <section className="sila-section py-16 sm:py-20" data-testid="tour-borghi-list">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
              <Ticket className="h-3.5 w-3.5" />Prenotazione online
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Scegli il tuo tour</h2>
            <p className="mt-3 text-muted-foreground">Ogni tour combina più esperienze in un itinerario guidato. Prenota il tuo posto in pochi clic.</p>
          </div>
        </Reveal>

        {tours === null ? (
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-96 w-full rounded-[24px]" />)}
          </div>
        ) : tours.length === 0 ? (
          <Card className="mx-auto mt-12 max-w-lg p-10 text-center" data-testid="tour-borghi-empty">
            <Castle className="mx-auto mb-3 h-9 w-9 text-muted-foreground" />
            <p className="font-display text-lg font-semibold">Nuovi tour in arrivo</p>
            <p className="mt-1 text-sm text-muted-foreground">Stiamo preparando itinerari indimenticabili tra i borghi silani. Torna presto!</p>
          </Card>
        ) : (
          <RevealGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {tours.map((t) => <TourCard key={t.id || t.slug} tour={t} />)}
          </RevealGroup>
        )}
      </section>
    </PublicLayout>
  );
}
